import { useState, useEffect, useCallback, useMemo } from "react";
import {
  LayoutMode,
  ViewSize,
  ResourceType,
} from "@/components/layout/gallery-layout/types";
import { ResourceGroup } from "@/models/resource-group";

/**
 * Option for groupBy dropdown
 */
export interface GroupByOption {
  value: string;
  label: string;
}

/**
 * Configuration for a resource gallery
 */
export interface ResourceGalleryConfig<T> {
  /** Type of resource (tracker, dashboard, etc.) */
  resourceType: ResourceType;
  /** Storage key prefix for localStorage */
  storageKey: string;
  /** Get display name for an item */
  getDisplayName: (item: T) => string;
  /** Get group key for an item based on current groupBy value */
  getGroupKey: (item: T, groupBy: string) => string;
  /** Available groupBy options */
  groupByOptions: GroupByOption[];
  /** Default groupBy value */
  defaultGroupBy: string;
  /** Filter function for search */
  filterFn: (item: T, searchTerm: string) => boolean;
  /** Default view mode */
  defaultViewMode?: LayoutMode;
  /** Default view size */
  defaultViewSize?: ViewSize;
  /** Available view modes (defaults to all) */
  availableViewModes?: LayoutMode[];
  /** Sort function for groups */
  sortGroups?: (a: string, b: string) => number;
  /** Get ID from item */
  getId: (item: T) => string;
}

/**
 * Options for the useResourceGallery hook
 */
export interface ResourceGalleryOptions {
  /** External groupBy value (overrides internal state) */
  externalGroupBy?: string;
  /** Resource groups for folder organization */
  resourceGroups?: ResourceGroup[];
}

/**
 * Return type for useResourceGallery hook
 */
export interface ResourceGalleryState<T> {
  // Search
  searchTerm: string;
  setSearchTerm: (value: string) => void;

  // Filtering
  filteredItems: T[];

  // Grouping
  groupBy: string;
  setGroupBy: (value: string) => void;
  groupedItems: Record<string, T[]>;
  collapsedGroups: Set<string>;
  toggleGroup: (groupKey: string) => void;
  setCollapsedGroups: (groups: Set<string>) => void;

  // View preferences
  viewMode: LayoutMode;
  setViewMode: (mode: LayoutMode) => void;
  viewSize: ViewSize;
  setViewSize: (size: ViewSize) => void;

  // Ordered IDs for range selection
  orderedIds: string[];

  // Folder support
  resourceGroups?: ResourceGroup[];
  expandedFolders: Set<string>;
  toggleFolder: (folderId: string) => void;
}

/**
 * Custom event name for groupBy changes
 */
const getGroupByEventName = (storageKey: string) =>
  `${storageKey}GroupByChange`;

/**
 * Generic hook for resource gallery functionality.
 * Handles search, filtering, grouping, and view preferences.
 *
 * @param items - Array of items to display
 * @param config - Configuration for the gallery
 * @param options - Optional settings like external groupBy
 * @returns Gallery state and actions
 */
export function useResourceGallery<T>(
  items: T[] | undefined,
  config: ResourceGalleryConfig<T>,
  options?: ResourceGalleryOptions,
): ResourceGalleryState<T> {
  const {
    storageKey,
    getGroupKey,
    groupByOptions,
    defaultGroupBy,
    filterFn,
    defaultViewMode = "grid",
    defaultViewSize = "medium",
    sortGroups,
    getId,
  } = config;

  const { externalGroupBy, resourceGroups } = options || {};

  // Search state
  const [searchTerm, setSearchTerm] = useState("");

  // GroupBy state with localStorage persistence
  const [groupBy, setGroupByState] = useState<string>(() => {
    if (typeof window === "undefined") return defaultGroupBy;
    const saved = localStorage.getItem(`${storageKey}GroupBy`);
    if (saved && groupByOptions.some((opt) => opt.value === saved)) {
      return saved;
    }
    return defaultGroupBy;
  });

  // View mode state with localStorage persistence
  const [viewMode, setViewModeState] = useState<LayoutMode>(() => {
    if (typeof window === "undefined") return defaultViewMode;
    const saved = localStorage.getItem(`${storageKey}ViewMode`) as LayoutMode;
    if (saved && ["grid", "list", "table", "board"].includes(saved)) {
      return saved;
    }
    return defaultViewMode;
  });

  // View size state with localStorage persistence
  const [viewSize, setViewSizeState] = useState<ViewSize>(() => {
    if (typeof window === "undefined") return defaultViewSize;
    const saved = localStorage.getItem(`${storageKey}ViewSize`) as ViewSize;
    if (saved && ["small", "medium", "large"].includes(saved)) {
      return saved;
    }
    return defaultViewSize;
  });

  // Collapsed groups state
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(
    new Set(),
  );

  // Expanded folders state with localStorage persistence
  const [expandedFolders, setExpandedFoldersState] = useState<Set<string>>(
    () => {
      if (typeof window === "undefined") return new Set<string>();
      const saved = localStorage.getItem(`${storageKey}ExpandedFolders`);
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          return new Set(parsed);
        } catch {
          return new Set<string>();
        }
      }
      return new Set<string>();
    },
  );

  // GroupBy setter with localStorage and event dispatch
  const setGroupBy = useCallback(
    (value: string) => {
      setGroupByState(value);
      localStorage.setItem(`${storageKey}GroupBy`, value);
      // Reset collapsed groups when groupBy changes
      setCollapsedGroups(new Set());
      // Dispatch custom event for same-tab synchronization
      window.dispatchEvent(
        new CustomEvent(getGroupByEventName(storageKey), { detail: value }),
      );
    },
    [storageKey],
  );

  // View mode setter with localStorage
  const setViewMode = useCallback(
    (mode: LayoutMode) => {
      setViewModeState(mode);
      localStorage.setItem(`${storageKey}ViewMode`, mode);
    },
    [storageKey],
  );

  // View size setter with localStorage
  const setViewSize = useCallback(
    (size: ViewSize) => {
      setViewSizeState(size);
      localStorage.setItem(`${storageKey}ViewSize`, size);
    },
    [storageKey],
  );

  // Toggle group collapse
  const toggleGroup = useCallback((groupKey: string) => {
    setCollapsedGroups((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(groupKey)) {
        newSet.delete(groupKey);
      } else {
        newSet.add(groupKey);
      }
      return newSet;
    });
  }, []);

  // Toggle folder expand/collapse with localStorage persistence
  const toggleFolder = useCallback(
    (folderId: string) => {
      setExpandedFoldersState((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(folderId)) {
          newSet.delete(folderId);
        } else {
          newSet.add(folderId);
        }
        // Save to localStorage
        localStorage.setItem(
          `${storageKey}ExpandedFolders`,
          JSON.stringify(Array.from(newSet)),
        );
        return newSet;
      });
    },
    [storageKey],
  );

  // Listen for storage changes (cross-tab sync)
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === `${storageKey}GroupBy` && e.newValue) {
        if (groupByOptions.some((opt) => opt.value === e.newValue)) {
          setGroupByState(e.newValue);
          setCollapsedGroups(new Set());
        }
      }
      if (e.key === `${storageKey}ExpandedFolders` && e.newValue) {
        try {
          const parsed = JSON.parse(e.newValue);
          setExpandedFoldersState(new Set(parsed));
        } catch {
          // Ignore invalid JSON
        }
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, [storageKey, groupByOptions]);

  // Listen for same-tab groupBy changes (custom event)
  useEffect(() => {
    const eventName = getGroupByEventName(storageKey);
    const handleCustomChange = (e: CustomEvent<string>) => {
      setGroupByState(e.detail);
      setCollapsedGroups(new Set());
    };

    window.addEventListener(eventName, handleCustomChange as EventListener);
    return () =>
      window.removeEventListener(
        eventName,
        handleCustomChange as EventListener,
      );
  }, [storageKey]);

  // Filter items based on search term
  const filteredItems = useMemo(() => {
    if (!items) return [];
    if (!searchTerm.trim()) return items;
    return items.filter((item) => filterFn(item, searchTerm));
  }, [items, searchTerm, filterFn]);

  // Effective groupBy (external takes precedence)
  const effectiveGroupBy = externalGroupBy ?? groupBy;

  // Group items
  const groupedItems = useMemo(() => {
    if (effectiveGroupBy === "none") {
      return { "": filteredItems };
    }

    const groups: Record<string, T[]> = {};

    filteredItems.forEach((item) => {
      const groupKey = getGroupKey(item, effectiveGroupBy);

      // Handle multiple groups (e.g., item belongs to multiple tags)
      const keys = Array.isArray(groupKey) ? groupKey : [groupKey];

      keys.forEach((key) => {
        if (!groups[key]) {
          groups[key] = [];
        }
        groups[key].push(item);
      });
    });

    // Sort keys
    const sortedKeys = Object.keys(groups).sort(
      sortGroups ||
        ((a, b) => {
          // Default: put special groups at the end
          if (a === "Sans tags" || a === "Autre" || a === "Inactifs") return 1;
          if (b === "Sans tags" || b === "Autre" || b === "Inactifs") return -1;
          return a.localeCompare(b);
        }),
    );

    const sortedGroups: Record<string, T[]> = {};
    sortedKeys.forEach((key) => {
      sortedGroups[key] = groups[key];
    });

    return sortedGroups;
  }, [filteredItems, effectiveGroupBy, getGroupKey, sortGroups]);

  // Ordered IDs for range selection
  const orderedIds = useMemo(() => {
    return filteredItems.map(getId);
  }, [filteredItems, getId]);

  return {
    // Search
    searchTerm,
    setSearchTerm,

    // Filtering
    filteredItems,

    // Grouping
    groupBy: effectiveGroupBy,
    setGroupBy,
    groupedItems,
    collapsedGroups,
    toggleGroup,
    setCollapsedGroups,

    // View preferences
    viewMode,
    setViewMode,
    viewSize,
    setViewSize,

    // Ordered IDs
    orderedIds,

    // Folder support
    resourceGroups,
    expandedFolders,
    toggleFolder,
  };
}

export default useResourceGallery;
